import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Trophy, 
  DollarSign, 
  Users, 
  CheckCircle, 
  ArrowRight, 
  Shield,
  Banknote,
  FileText,
  ChevronRight,
  Loader2,
  Calendar,
  AlertCircle,
  ShoppingBag,
  Gift
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const TournamentOnboarding = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [accepting, setAccepting] = useState(false);
  const [verifyingAge, setVerifyingAge] = useState(false);
  
  // Age verification state
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [confirm18Plus, setConfirm18Plus] = useState(false);
  const [acknowledgeTerms, setAcknowledgeTerms] = useState(false);
  const [ageError, setAgeError] = useState(null);
  
  // Check if we should start at age verification step
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam === 'age') {
      setStep(4); // Jump to age verification
    }
  }, [searchParams]);

  const handleAcceptRules = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/tournament/accept-rules`, { accepted: true });
      toast.success('Tournament rules accepted!');
      setStep(4); // Move to age verification
    } catch (error) {
      console.error('Error accepting rules:', error);
      toast.error('Failed to accept rules. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleVerifyAge = async () => {
    setVerifyingAge(true);
    setAgeError(null);
    
    try {
      const res = await axios.post(`${API}/tournament/verify-age`, {
        date_of_birth: dateOfBirth,
        confirm_18_plus: confirm18Plus,
        acknowledge_terms: acknowledgeTerms
      });
      
      if (res.data.success) {
        toast.success('Age verified! You can now join cash tournaments.');
        if (onComplete) {
          onComplete();
        } else {
          navigate('/wallet');
        }
      } else if (res.data.error === 'underage') {
        setAgeError(res.data);
      }
    } catch (error) {
      console.error('Error verifying age:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify age');
    } finally {
      setVerifyingAge(false);
    }
  };

  const handleViewFullRules = () => {
    navigate('/tournament-rules');
  };

  // Calculate age from DOB for display
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculatedAge = calculateAge(dateOfBirth);
  const isUnder18 = calculatedAge !== null && calculatedAge < 18;

  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[var(--ps-text-primary)] mb-2">
            Tournament Games
          </h1>
          <p className="text-[var(--ps-text-muted)]">
            Play for real money prizes
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${
                step >= s ? 'bg-green-500' : 'bg-[var(--ps-border)]'
              }`}
            />
          ))}
        </div>

        {/* Main Card */}
        <div className="bg-[var(--ps-bg-surface)] rounded-2xl border border-[var(--ps-border)] overflow-hidden">
          
          {/* Step 1: How It Works */}
          {step === 1 && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[var(--ps-text-primary)] mb-6 text-center">
                How Tournaments Work
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 p-4 bg-[var(--ps-bg-elevated)] rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-500 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--ps-text-primary)]">Pay Entry Fee</h3>
                    <p className="text-sm text-[var(--ps-text-muted)]">
                      Your entry fee goes into the prize pool
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[var(--ps-bg-elevated)] rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-500 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--ps-text-primary)]">Play Your Round</h3>
                    <p className="text-sm text-[var(--ps-text-muted)]">
                      Skill determines the winner
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[var(--ps-bg-elevated)] rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-500 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--ps-text-primary)]">Win Prizes</h3>
                    <p className="text-sm text-[var(--ps-text-muted)]">
                      Winners split the prize pool
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setStep(2)} 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6"
                data-testid="onboarding-continue-1"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Example */}
          {step === 2 && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[var(--ps-text-primary)] mb-6 text-center">
                Example: 4-Player Skins
              </h2>

              <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-5 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[var(--ps-text-muted)]">Entry Fee</span>
                  <span className="text-[var(--ps-text-primary)] font-medium">$25.00 x 4</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[var(--ps-text-muted)]">Prize Pool</span>
                  <span className="text-[var(--ps-text-primary)] font-semibold">$100.00</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[var(--ps-text-muted)]">Platform Fee (10%)</span>
                  <span className="text-red-400">-$10.00</span>
                </div>
                <div className="border-t border-[var(--ps-border)] my-3"></div>
                <div className="flex justify-between items-center">
                  <span className="text-[var(--ps-text-primary)] font-semibold">Total Prizes</span>
                  <span className="text-green-500 font-bold text-xl">$90.00</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-5 mb-6 border border-green-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <Trophy className="w-5 h-5 text-green-500" />
                  <span className="font-semibold text-[var(--ps-text-primary)]">You won 5 of 10 skins!</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[var(--ps-text-muted)]">Your Prize</span>
                  <span className="text-green-500 font-bold text-2xl">$45.00</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1 border-[var(--ps-border)]"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="onboarding-continue-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Key Points & Accept Rules */}
          {step === 3 && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[var(--ps-text-primary)] mb-6 text-center">
                Important Information
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[var(--ps-text-primary)]">
                    Winnings deposited to your prize balance
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
                  <Banknote className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[var(--ps-text-primary)]">
                    Withdraw anytime to your bank (2-3 days)
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
                  <Shield className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[var(--ps-text-primary)]">
                    Secure payments via Stripe
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
                  <FileText className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-[var(--ps-text-primary)]">
                    Tax docs provided for $600+/year
                  </span>
                </div>
              </div>

              <button
                onClick={handleViewFullRules}
                className="w-full flex items-center justify-between p-4 bg-[var(--ps-bg-elevated)] rounded-xl mb-6 hover:bg-[var(--ps-bg-hover)] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[var(--ps-text-muted)]" />
                  <span className="text-[var(--ps-text-primary)]">View Full Tournament Rules</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[var(--ps-text-muted)]" />
              </button>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)}
                  className="flex-1 border-[var(--ps-border)]"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleAcceptRules}
                  disabled={accepting}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="accept-rules-btn"
                >
                  {accepting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      I Accept
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Age Verification */}
          {step === 4 && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[var(--ps-text-primary)] mb-2 text-center">
                Age Verification
              </h2>
              <p className="text-[var(--ps-text-muted)] text-sm text-center mb-6">
                Required for cash tournament participation
              </p>

              {/* Age Restriction Notice */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-[var(--ps-text-primary)] font-medium mb-1">
                      Must be 18 or older for cash tournaments
                    </p>
                    <p className="text-[var(--ps-text-muted)]">
                      Minors can still enjoy Golf Coins for merchandise, apparel, and golf gear!
                    </p>
                  </div>
                </div>
              </div>

              {/* Underage Message */}
              {ageError && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[var(--ps-text-primary)] font-medium mb-2">
                        Golf Coins Available for You!
                      </p>
                      <p className="text-[var(--ps-text-muted)] text-sm mb-3">
                        While cash tournaments require you to be 18+, you can still:
                      </p>
                      <ul className="text-sm text-[var(--ps-text-muted)] space-y-1">
                        <li className="flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4 text-blue-400" />
                          Purchase hats, shirts & golf gear
                        </li>
                        <li className="flex items-center gap-2">
                          <Trophy className="w-4 h-4 text-blue-400" />
                          Play in Golf Coin tournaments
                        </li>
                        <li className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-400" />
                          Join rounds with friends
                        </li>
                      </ul>
                      <Button 
                        onClick={() => navigate('/golf-coins')}
                        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white w-full"
                      >
                        Explore Golf Coins
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Date of Birth Input */}
              {!ageError && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[var(--ps-text-primary)] mb-2">
                      Date of Birth
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ps-text-muted)]" />
                      <input
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="w-full pl-10 pr-4 py-3 bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-xl text-[var(--ps-text-primary)] focus:outline-none focus:ring-2 focus:ring-green-500"
                        data-testid="dob-input"
                      />
                    </div>
                    {calculatedAge !== null && (
                      <p className={`text-sm mt-2 ${isUnder18 ? 'text-amber-500' : 'text-green-500'}`}>
                        {isUnder18 
                          ? `Age ${calculatedAge} - Under 18, cash tournaments not available`
                          : `Age ${calculatedAge} - Eligible for cash tournaments`
                        }
                      </p>
                    )}
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-3 mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={confirm18Plus}
                        onChange={(e) => setConfirm18Plus(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-[var(--ps-border)] text-green-600 focus:ring-green-500"
                        data-testid="confirm-18-checkbox"
                      />
                      <span className="text-sm text-[var(--ps-text-primary)]">
                        I confirm that I am 18 years of age or older
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acknowledgeTerms}
                        onChange={(e) => setAcknowledgeTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded border-[var(--ps-border)] text-green-600 focus:ring-green-500"
                        data-testid="acknowledge-terms-checkbox"
                      />
                      <span className="text-sm text-[var(--ps-text-primary)]">
                        I understand that providing false information about my age may result in account suspension and forfeiture of any winnings
                      </span>
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setStep(3)}
                      className="flex-1 border-[var(--ps-border)]"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleVerifyAge}
                      disabled={verifyingAge || !dateOfBirth || !confirm18Plus || !acknowledgeTerms || isUnder18}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                      data-testid="verify-age-btn"
                    >
                      {verifyingAge ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          Verify & Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Skip Option */}
        {onSkip && step < 4 && (
          <div className="text-center mt-6">
            <button
              onClick={onSkip}
              className="text-[var(--ps-text-muted)] text-sm hover:text-[var(--ps-text-primary)] transition-colors"
            >
              Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentOnboarding;
